/**
 * Oblikovanje brojeva i datuma po jeziku sučelja.
 *
 * Cijene se prikazuju BEZ oznake valute: valuta je svojstvo resorta i stoji na
 * `/sys/tenants`, a `/admin/*` je ne vraća (API §5). Izmišljati €  značilo bi
 * lagati resortu koji radi u kunama ili dolarima.
 */
export function formatirajIznos(iznos: number, jezik: string): string {
  return new Intl.NumberFormat(jezik, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(iznos)
}

/**
 * Cijena gostu ide S valutom: nju, za razliku od `/admin/*`, poslužitelj na
 * površini gosta uvijek šalje uz iznos (`quote`, `booking`). Nepoznat kod valute
 * ne smije srušiti ekran — tada ostaje broj i kod uz njega.
 */
export function formatirajCijenu(iznos: number, valuta: string, jezik: string): string {
  try {
    return new Intl.NumberFormat(jezik, { style: 'currency', currency: valuta }).format(iznos)
  } catch {
    return `${formatirajIznos(iznos, jezik)} ${valuta}`
  }
}

/**
 * 24-SATNI SAT JE STANDARD CIJELE APLIKACIJE (dotjerivanje-4 §3).
 *
 * **`Intl` bez ovoga sluša JEZIK, a `en` je 12-satni**: isti trenutak koji na
 * hrvatskom sučelju piše "18:30" na engleskom je pisao "6:30 PM" — i to ne samo
 * gostu nego i vozaču, pultu i editoru, jer svaki od njih čita isti račun. Sat
 * nije stvar jezika nego mjere: raspored resorta, smjena i rok naloga pišu se
 * jednako bez obzira na to kojim se jezikom uz njih govori.
 *
 * **Zato `hourCycle: 'h23'`, a ne `hour12: false`**: potonji u dijelu izvedbi
 * daje `h24` ("24:00" umjesto "00:00"), pa bi ponoć bila sat koji ne postoji.
 * Imena mjeseci i dana ostaju na jeziku sučelja — i18n vrijedi za RIJEČI, ne za
 * brojčanik (`dateStyle` ih i dalje daje).
 *
 * Ovo je JEDINO mjesto na kojem se sat oblikuje; tko treba sat, uzima odavde
 * (`lib/termin.ts` ga računa iz UTC polja i time ga već ima).
 */
export const SAT_24: Intl.DateTimeFormatOptions = { timeStyle: 'short', hourCycle: 'h23' }

/** Sat iz gotovog `Date`, po istoj mjeri kao sve ostalo ("18:30"). */
export function sat24(vrijeme: Date, jezik: string): string {
  return new Intl.DateTimeFormat(jezik, SAT_24).format(vrijeme)
}

/**
 * Jezik BROJČANIKA nativnog polja (dotjerivanje-4 §3) — druga polovica istog
 * pravila.
 *
 * `<input type="time">` i `datetime-local` ne crta naš CSS nego preglednik, i
 * crta ih po jeziku POLJA: u engleskom sučelju isti sat nudi kao "06:30 PM". Sat
 * je mjera, ne jezik (vidi {@link SAT_24}), pa satna polja nose jezik čiji je
 * brojčanik 24-satni — vrijednost je i inače uvijek `HH:mm`, ovo je samo ono što
 * korisnik vidi.
 *
 * Popravak je najbolji mogući, a ne potpun: `lang` sluša Chromium, dok Firefox i
 * iOS čitaju postavku sustava. Ono što o nama ovisi — svaki PRIKAZ kroz
 * aplikaciju i svaka vrijednost koja putuje — 24-satno je bez iznimke.
 *
 * Pitaju ga OBA obrasca aplikacije (`components/noir.tsx` za gosta i vozača,
 * `components/Field.tsx` za konzolu), pa stoji ovdje, uz sat.
 */
export function jezikBrojcanika(vrsta: string | undefined): string | undefined {
  return vrsta === 'time' || vrsta === 'datetime-local' ? 'hr' : undefined
}

/** `DateOnly` s poslužitelja je `YYYY-MM-DD` — bez vremena i bez vremenske zone. */
export function formatirajDatum(datum: string | null | undefined, jezik: string): string | null {
  if (!datum) return null

  const [godina, mjesec, dan] = datum.split('-').map(Number)
  if (!godina || !mjesec || !dan) return datum

  return new Intl.DateTimeFormat(jezik, { dateStyle: 'medium' }).format(
    new Date(Date.UTC(godina, mjesec - 1, dan)),
  )
}

/** Samo sat i minuta — timeline vožnje je događaj unutar dana, ne datum. */
export function formatirajSat(trenutak: string | null | undefined, jezik: string): string | null {
  if (!trenutak) return null

  const vrijeme = new Date(trenutak)
  if (Number.isNaN(vrijeme.getTime())) return trenutak

  return sat24(vrijeme, jezik)
}

/** Trenutak s poslužitelja je UTC (CLAUDE.md §16) — prikazuje se u zoni preglednika. */
export function formatirajTrenutak(
  trenutak: string | null | undefined,
  jezik: string,
): string | null {
  if (!trenutak) return null

  const vrijeme = new Date(trenutak)
  if (Number.isNaN(vrijeme.getTime())) return trenutak

  return new Intl.DateTimeFormat(jezik, { dateStyle: 'medium', ...SAT_24 }).format(vrijeme)
}
